import React, { useMemo, useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { APPNAME, APPOWNER } from "../utils/utils.constants";
import { routes } from "../utils/utils.routes";
import { toast } from "react-toastify";
import { useLanguage } from "../contexts/LanguageContext";
import { LanguageSwitcher } from "./subcomponents/LanguageSwitcher";
import { AdminPageLoader } from "./admin/AdminUI";
import logo from "../assets/logo.png";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { cn } from "../lib/utils";
import {
  FiGrid, FiFileText, FiCalendar, FiActivity, FiClock, FiCheckSquare,
  FiMail, FiMessageSquare, FiUsers, FiLogOut, FiMenu, FiX, FiHome, FiBook,
  FiHelpCircle, FiLayers, FiChevronDown, FiChevronRight, FiChevronsLeft,
  FiBarChart2, FiLogIn, FiHash, FiClipboard, FiDollarSign,
} from "react-icons/fi";

const I = (C: any) => C as any;
const GridI = I(FiGrid); const FileI = I(FiFileText); const CalI = I(FiCalendar);
const ActI = I(FiActivity); const ClockI = I(FiClock); const CheckI = I(FiCheckSquare);
const MailI = I(FiMail); const MsgI = I(FiMessageSquare); const UsersI = I(FiUsers);
const LogoutI = I(FiLogOut); const MenuI = I(FiMenu); const XI = I(FiX);
const HomeI = I(FiHome); const BookI = I(FiBook); const HelpI = I(FiHelpCircle);
const LayersI = I(FiLayers);
const ChevronI = I(FiChevronDown); const ChevronRightI = I(FiChevronRight);
const CollapseI = I(FiChevronsLeft);
const ChartI = I(FiBarChart2); const LoginI = I(FiLogIn); const HashI = I(FiHash);
const ClipboardI = I(FiClipboard);
const DollarI = I(FiDollarSign);

export const ADMIN_BASE = "/admin";

const getInitials = (name?: string) =>
  (name || "?").split(" ").filter(Boolean).slice(0, 1).map((p) => p[0]?.toUpperCase()).join("");

const academicYears = () => {
  const y = new Date().getFullYear();
  return [`${y - 1}-${y}`, `${y}-${y + 1}`, `${y + 1}-${y + 2}`];
};

type NavItem = { to: string; icon: React.ReactNode; label: string; end?: boolean; slug?: string };
type NavSection = { id: string; title?: string; items: NavItem[] };

export const AdminLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { user, isAdmin, isCenterPublisher, loading, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [academicYear, setAcademicYear] = useState(academicYears()[0]);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    academic: true, research: true, web: true, admin: true,
  });

  const slug = location.pathname.replace(ADMIN_BASE, "").replace(/^\//, "") || "overview";

  const pageTitle = useMemo(() => {
    const map: Record<string, string> = {
      "": t.admin.overview, overview: t.admin.overview, articles: t.admin.articlesPosts,
      events: t.admin.events, activities: t.admin.activitiesEvents, schedules: t.admin.schedules,
      tenders: t.admin.tenders,
      centers: t.admin.researchCenters, faculties: t.admin.faculties, faqs: t.admin.faqs,
      "key-facts": t.admin.keyFacts, "admission-guide": t.admin.admissionGuide,
      "academic-fees": t.admin.academicFees,
      pending: t.admin.pending, newsletter: t.admin.newsletter, messages: t.admin.messages,
      users: t.admin.users, categories: t.admin.categories, staff: t.admin.staff,
      "special-programs": t.admin.specialPrograms,
      visits: t.admin.visits, "login-history": t.admin.loginHistory,
    };
    return map[slug] || t.admin.dashboard;
  }, [slug, t]);

  if (loading) return <div className="admin-shell"><AdminPageLoader /></div>;
  if (!user) return <Navigate to={routes.LOGIN} replace />;

  const handleLogout = () => { logout(); toast.info(t.admin.logout); navigate(routes.HOME); };

  const toggleSection = (id: string) =>
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));

  const publisherWeb: NavItem[] = [
    { to: "/articles", icon: <FileI size={17} />, label: t.admin.articlesPosts, slug: "articles" },
    { to: "/events", icon: <CalI size={17} />, label: t.admin.events, slug: "events" },
    { to: "/activities", icon: <ActI size={17} />, label: t.admin.activities, slug: "activities" },
    ...(isCenterPublisher ? [] : [{ to: "/tenders", icon: <FileI size={17} />, label: t.admin.tenders, slug: "tenders" }]),
  ];

  const navSections: NavSection[] = [
    { id: "root", items: [{ to: "", icon: <GridI size={17} />, label: t.admin.overview, end: true, slug: "overview" }] },
    ...(isAdmin ? [{
      id: "academic",
      title: t.admin.sectionAcademic,
      items: [
        // { to: "/faculties", icon: <LayersI size={17} />, label: t.admin.domains, slug: "faculties" },
        { to: "/faculties", icon: <BookI size={17} />, label: t.admin.faculties, slug: "faculties" },
        { to: "/staff", icon: <UsersI size={17} />, label: t.admin.staff, slug: "staff" },
        { to: "/special-programs", icon: <LayersI size={17} />, label: t.admin.specialPrograms, slug: "special-programs" },
      ],
    }] : []),
    ...((isAdmin || !isCenterPublisher) ? [{
      id: "research",
      title: t.admin.sectionResearch,
      items: [{ to: "/centers", icon: <ActI size={17} />, label: t.admin.researchCenters, slug: "centers" }],
    }] : []),
    {
      id: "web",
      title: t.admin.sectionWeb,
      items: isAdmin ? [
        { to: "/articles", icon: <FileI size={17} />, label: t.admin.articlesPosts, slug: "articles" },
        // { to: "/articles", icon: <TagI size={17} />, label: t.admin.categories, slug: "articles" },
        { to: "/events", icon: <CalI size={17} />, label: t.admin.events, slug: "events" },
        { to: "/activities", icon: <ActI size={17} />, label: t.admin.activities, slug: "activities" },
        { to: "/tenders", icon: <FileI size={17} />, label: t.admin.tenders, slug: "tenders" },
        { to: "/faqs", icon: <HelpI size={17} />, label: t.admin.faqs, slug: "faqs" },
        { to: "/key-facts", icon: <HashI size={17} />, label: t.admin.keyFacts, slug: "key-facts" },
        { to: "/partners", icon: <UsersI size={17} />, label: t.admin.sitePartners, slug: "partners" },
        { to: "/admission-guide", icon: <ClipboardI size={17} />, label: t.admin.admissionGuide, slug: "admission-guide" },
        { to: "/academic-fees", icon: <DollarI size={17} />, label: t.admin.academicFees, slug: "academic-fees" },
      ] : publisherWeb,
    },
    ...(!isAdmin && !isCenterPublisher ? [{ id: "schedules", items: [{ to: "/schedules", icon: <ClockI size={17} />, label: t.admin.schedules, slug: "schedules" }] }] : []),
    ...(isAdmin ? [{
      id: "admin",
      title: t.admin.sectionAdmin,
      items: [
        { to: "/schedules", icon: <ClockI size={17} />, label: t.admin.schedules, slug: "schedules" },
        { to: "/users", icon: <UsersI size={17} />, label: t.admin.users, slug: "users" },
        { to: "/visits", icon: <ChartI size={17} />, label: t.admin.visits, slug: "visits" },
        { to: "/login-history", icon: <LoginI size={17} />, label: t.admin.loginHistory, slug: "login-history" },
        { to: "/pending", icon: <CheckI size={17} />, label: t.admin.pending, slug: "pending" },
        { to: "/newsletter", icon: <MailI size={17} />, label: t.admin.newsletter, slug: "newsletter" },
        { to: "/messages", icon: <MsgI size={17} />, label: t.admin.messages, slug: "messages" },
      ],
    }] : []),
  ];

  const renderNavLink = (it: NavItem, sectionId: string) => (
    <NavLink
      key={`${sectionId}-${it.label}-${it.to}`}
      end={it.end ?? it.to === ""}
      to={`${ADMIN_BASE}${it.to}`}
      onClick={() => setSidebarOpen(false)}
      title={sidebarCollapsed ? it.label : undefined}
      data-testid={`admin-nav-${it.slug || (it.to || "overview").replace(/^\//, "")}`}
      className={({ isActive }) => cn("admin-sidebar__link", isActive && "active", sidebarCollapsed && "admin-sidebar__link--collapsed")}
    >
      {it.icon}
      {!sidebarCollapsed && <span>{it.label}</span>}
    </NavLink>
  );

  return (
    <div data-testid="admin-layout" className={cn("admin-shell", sidebarCollapsed && "admin-shell--collapsed")}>
      {sidebarOpen && <div className="admin-sidebar-overlay" onClick={() => setSidebarOpen(false)} aria-hidden />}
      <aside data-testid="admin-sidebar" className={cn("admin-sidebar", sidebarOpen && "open", sidebarCollapsed && "admin-sidebar--collapsed")}>
        <div className="admin-sidebar__brand">
          <div className="admin-sidebar__brand-row">
            {!sidebarCollapsed && (
              <div className="admin-sidebar__brand-logo">
                <img src={logo} alt={APPNAME} width={36} height={36} />
                <div>
                  <div className="admin-sidebar__brand-name">{APPNAME}</div>
                  <div className="admin-sidebar__brand-badge">{t.admin.siteDashboard}</div>
                </div>
              </div>
            )}
            <button type="button" className="admin-sidebar-close" onClick={() => setSidebarOpen(false)} aria-label="Fermer">
              <XI size={20} />
            </button>
          </div>
          <button
            type="button"
            className="admin-sidebar__collapse-btn"
            onClick={() => setSidebarCollapsed((v) => !v)}
            aria-label={sidebarCollapsed ? "Expand menu" : "Collapse menu"}
          >
            <CollapseI size={18} className={sidebarCollapsed ? "rotate-180" : ""} />
          </button>
        </div>

        <nav className="admin-sidebar__nav">
          {navSections.map((section) => (
            <div key={section.id} className="admin-sidebar__section">
              {section.title && !sidebarCollapsed ? (
                <Collapsible open={openSections[section.id] !== false} onOpenChange={() => toggleSection(section.id)}>
                  <CollapsibleTrigger className="admin-sidebar__section-trigger">
                    <span>{section.title}</span>
                    <ChevronRightI size={14} className={cn("admin-sidebar__chevron", openSections[section.id] !== false && "open")} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="admin-sidebar__section-content">
                    {section.items.map((it) => renderNavLink(it, section.id))}
                  </CollapsibleContent>
                </Collapsible>
              ) : (
                section.items.map((it) => renderNavLink(it, section.id))
              )}
            </div>
          ))}
        </nav>

        <div className="admin-sidebar__footer">
          {!sidebarCollapsed && (
            <>
              <div className="admin-sidebar__user">
                <div className="admin-sidebar__avatar">{getInitials(user.name)}</div>
                <div className="admin-sidebar__user-info">
                  <div className="admin-sidebar__user-name">{user.name}</div>
                  <div className="admin-sidebar__user-email">{user.email}</div>
                </div>
              </div>
              <Link to={routes.HOME} className="admin-sidebar__footer-link"><HomeI size={14} /> {t.admin.viewSite}</Link>
            </>
          )}
          <button type="button" data-testid="admin-logout" className="admin-sidebar__logout" onClick={handleLogout} title={t.admin.logout}>
            <LogoutI size={14} /> {!sidebarCollapsed && t.admin.logout}
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <header className="admin-topbar">
          <div className="admin-topbar__left">
            <button type="button" data-testid="admin-sidebar-toggle" className="admin-sidebar-toggle" onClick={() => setSidebarOpen(true)} aria-label="Menu">
              <MenuI size={22} />
            </button>
            <div>
              <div className="admin-topbar__eyebrow">{t.admin.dashboard.toUpperCase()}</div>
              <h1 className="admin-topbar__title">{slug === "overview" || slug === "" ? APPOWNER : pageTitle}</h1>
            </div>
          </div>
          <div className="admin-topbar__right">
            <LanguageSwitcher />
            <div className="admin-year-select">
              <select disabled value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} aria-label={t.admin.academicYear}>
                {academicYears().map((y) => (
                  <option key={y} value={y}>{t.admin.academicYear} {y}</option>
                ))}
              </select>
              <ChevronI size={14} className="admin-year-select__icon" />
            </div>
          </div>
        </header>
        <main className="admin-content"><Outlet /></main>
      </div>
    </div>
  );
};
